// Types and interfaces
// API interactions
import { useEffect, useState } from 'react'

import { encodeEventTopics } from 'viem'
import { useChainId, useSwitchChain, useWriteContract } from 'wagmi'

import { apiNewPostReceiveCctpMessage, apiSendOrderData } from '@src/api/order.api'
import { apiGetRateLimitConfig } from '@src/api/point.api'
import { WRAPPED_GAS_TOKENS } from '@src/configs/chainInfo'
// Enums
import { ExchangeType, LiquiditySource, ResultCode, StepNames } from '@src/enums'
import { useApproveToken } from '@src/hooks/evm/useApproveToken'
// Hooks specific to operations
import { useDepositWeth } from '@src/hooks/evm/useDepositWeth'
import { useWithdrawWeth } from '@src/hooks/evm/useWithdrawWeth'
import { useWaitForTransaction } from '@src/hooks/useWaitForTransaction'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
// Types
import { ChainHandler, EvmSwapParams, IExchangeData, UpdateData } from '@src/types/exchange'
import { IQuoteData } from '@src/types/order'
import { isFeatureEnabled } from '@src/utils'
import { getChecksumAddress, isEVMAddress } from '@src/utils/address'
import { getSwapFunctionParameters } from '@src/utils/exchangeUtils'
import { getAllowance } from '@src/utils/getAllowance'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
// Utility functions
import { createContextLogger } from '@src/utils/logger'
import { processReceipt } from '@src/utils/processReceipt'
import { mapTokenAddress } from '@src/utils/tokenAddressMapping'
import { useConfigStore } from '@src/zustand_stores'
// Add Zustand store
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useHistoryStore } from '@src/zustand_stores/history.store'

const logger = createContextLogger('useExchangeTokenEvm')

// Testnet only: Hook to fetch rate limit configuration
const useRateLimitConfig = () => {
    const [rateLimitConfig, setRateLimitConfig] = useState<{
        windowSeconds: number
        maxRequests: number
        cleanupInterval: number
    }>({
        windowSeconds: 30, // fallback
        maxRequests: 1,
        cleanupInterval: 60,
    })

    useEffect(() => {
        const fetchRateLimitConfig = async () => {
            try {
                const config = await apiGetRateLimitConfig()
                setRateLimitConfig({
                    windowSeconds: config.window_seconds,
                    maxRequests: config.max_requests_per_window,
                    cleanupInterval: config.cleanup_interval_seconds,
                })
                logger.info('Fetched rate limit config:', config)
            } catch (error) {
                logger.warn('Failed to fetch rate limit config, using fallback:', error)
                // Keep the default fallback values
            }
        }

        fetchRateLimitConfig()
    }, [])

    return rateLimitConfig
}

export function useExchangeTokenEvm(swapContract: any): ChainHandler {
    // Replace Redux selector with Zustand
    const referralCode = useApplicationStore(state => state.generatedReferralCode)
    const { setOrderId, setWaitingForConnectedWalletInteraction } = useApplicationStore()
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const { approveToken, waitForApproval } = useApproveToken()
    const { depositWeth, waitForDeposit } = useDepositWeth()
    const { withdrawWeth, waitForWithdraw } = useWithdrawWeth()
    const { waitForTransactionReceipt } = useWaitForTransaction()
    const { writeContractAsync } = useWriteContract()
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const { destinationAddress } = useExchangeDataContext()
    const chainId = useChainId()
    const { switchChainAsync } = useSwitchChain()
    const { solanaWalletAddress } = useWalletConnections()
    const rateLimitConfig = useRateLimitConfig()

    const handleTokenApproval = async (
        tokenAddress: string,
        spenderAddress: string,
        amount: bigint,
        updateData: UpdateData,
        sellData: IExchangeData
    ): Promise<void> => {
        logger.info('Initiating token approval', {
            tokenAddress,
            spenderAddress,
            amount,
        })

        try {
            logger.info('[handleTokenApproval]', tokenAddress, spenderAddress, amount)
            const hash = await approveToken(tokenAddress, spenderAddress, amount)

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.APPROVE_TOKENS_PENDING,
                result: {
                    ...updateData.result,
                    message: 'Waiting for approval confirmation.',
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            await waitForApproval(hash)
            logger.info('Token approval confirmed', { hash })
        } catch (error) {
            logger.error(`Token approval failed (chainId: ${sellData.chainId})`, error)
            throw new Error('Token approval failed. Please try again.')
        }
    }

    const handleApproval = async (
        updateData: UpdateData,
        sellData: IExchangeData,
        walletAddress: string
    ): Promise<void> => {
        try {
            const mappedAddress = mapTokenAddress(sellData.token?.address, true, +sellData.chainId)
            if (!mappedAddress) {
                throw new Error('Invalid token address')
            }
            const tokenAddress = isFeatureEnabled('USE_TESTNETS_ONLY')
                ? getChecksumAddress(mappedAddress)
                : mappedAddress

            const allowance = await getAllowance(+sellData.chainId, tokenAddress, walletAddress, swapContract.address)

            if (allowance >= sellData.value) {
                logger.info('Sufficient allowance exists', {
                    allowance,
                    required: sellData.value,
                })
                return
            }

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.APPROVE_TOKENS,
                result: { ...updateData.result, message: 'Please approve tokens.' },
            })

            try {
                const maxUint256 = BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff')
                await handleTokenApproval(tokenAddress, swapContract.address, maxUint256, updateData, sellData)
            } catch (approvalError: any) {
                // Check for user rejection
                if (
                    approvalError?.message?.includes('user rejected') ||
                    approvalError?.message?.includes('User denied')
                ) {
                    throw new Error('User rejected the approval transaction')
                }

                // Handle other approval errors
                throw new Error(`Token approval failed: ${approvalError.message}`)
            }
        } catch (error) {
            logger.error(`Approval process failed (chainId: ${sellData.chainId})`, error)
            throw error
        }
    }

    const handleWrap = async (amount: bigint, updateData: UpdateData, sellData: IExchangeData): Promise<string> => {
        try {
            logger.info('Initiating wrap operation', {
                amount,
                tokenAddress: WRAPPED_GAS_TOKENS[sellData.chainId].address,
            })

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.DEPOSIT_WETH,
                result: { ...updateData.result, message: `Depositing ${amount} gas.` },
            })

            const wrappedToken = WRAPPED_GAS_TOKENS[sellData.chainId]
            if (wrappedToken && isEVMAddress(wrappedToken.address)) {
                const tokenAddress = isFeatureEnabled('USE_TESTNETS_ONLY')
                    ? getChecksumAddress(wrappedToken.address)
                    : wrappedToken.address
                const hash = await depositWeth(tokenAddress, BigInt(amount))

                updateHistory(null, {
                    ...updateData,
                    currentStep: StepNames.DEPOSIT_WETH_PENDING,
                    result: {
                        ...updateData.result,
                        message: `Depositing ${amount} gas.`,
                        sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                    },
                })

                await waitForDeposit(hash)
                logger.success('Wrap operation completed', { hash })

                return hash
            } else {
                logger.fatal(`No wrapped gas token contract found for chainId ${sellData.chainId}`)
                throw new Error(
                    `Error wrapping gas asset: No wrapped gas token contract found for chainId ${sellData.chainId}`
                )
            }
        } catch (error) {
            logger.error(`Wrap operation failed (chainId: ${sellData.chainId})`, error)
            throw new Error('Error wrapping gas asset. Please try again.')
        }
    }

    const handleUnwrap = async (amount: bigint, updateData: UpdateData, sellData: IExchangeData): Promise<string> => {
        try {
            logger.info('Initiating unwrap operation', {
                amount,
                tokenAddress: WRAPPED_GAS_TOKENS[sellData.chainId].address,
            })

            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.UNWRAP_WETH,
                result: { ...updateData.result, message: `Unwrapping ${amount} gas.` },
            })

            const wrappedToken = WRAPPED_GAS_TOKENS[sellData.chainId]
            if (!wrappedToken || !isEVMAddress(wrappedToken.address)) {
                logger.fatal(`No wrapped gas token contract found for chainId ${sellData.chainId}`)
                throw new Error(
                    `Error unwrapping gas asset: No wrapped gas token contract found for chainId ${sellData.chainId}`
                )
            }

            const tokenAddress = isFeatureEnabled('USE_TESTNETS_ONLY')
                ? getChecksumAddress(wrappedToken.address)
                : wrappedToken.address

            const hash = await withdrawWeth(tokenAddress, BigInt(amount))
            logger.info('Updating history with currentStep=UNWRAP_WETH_PENDING...')
            updateHistory(null, {
                ...updateData,
                currentStep: StepNames.UNWRAP_WETH_PENDING,
                result: {
                    ...updateData.result,
                    message: `Unwrapping ${amount} gas.`,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            await waitForWithdraw(hash)
            logger.info('Unwrap operation completed', { hash })

            return hash
        } catch (error) {
            logger.error(`Unwrap operation failed (chainId: ${sellData.chainId})`, error)
            throw new Error('Error unwrapping gas asset. Please try again.')
        }
    }

    const handleCctpExchange = async (
        hash: string,
        exchangeId: string,
        sellData: IExchangeData,
        buyData: IExchangeData
    ) => {
        try {
            logger.info('Handling CCTP exchange', { hash })

            const result = await apiNewPostReceiveCctpMessage(
                availableNetworks[sellData.chainId].name,
                hash,
                availableNetworks[buyData.chainId].name?.toLowerCase() === 'solana' ? solanaWalletAddress : undefined
            )

            updateHistory(exchangeId, {
                currentStep: StepNames.MATCH_CREATED,
                result: {
                    status: ResultCode.pending,
                    message: 'Order accepted. Allow 5-20 minutes for matching.',
                    transactionETA: result?.eta || 20,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            return result
        } catch (error) {
            logger.error(`CCTP exchange failed (chainId: ${sellData.chainId})`, error)
            throw error
        }
    }

    const executeSwap = async (params: EvmSwapParams, referrer?: string) => {
        const { sellData, buyData, quote, exchangeId, liquiditySource, onSuccess } = params

        try {
            logger.info('Initiating EVM swap', { params })

            // Check if we need to switch networks
            if (chainId !== sellData.chainId) {
                logger.info('Switching network', {
                    from: chainId,
                    to: sellData.chainId,
                })

                await switchChainAsync({ chainId: sellData.chainId })
            }

            updateHistory(null, {
                exchangeId,
                currentStep: StepNames.CONFIRM_SWAP,
                result: {
                    status: ResultCode.pending,
                    message: 'Please confirm swap.',
                },
            })

            const exchangeType =
                liquiditySource === LiquiditySource.cctpDirect ? ExchangeType.cctp : ExchangeType.order_book
            const functionName = exchangeType === ExchangeType.cctp ? 'depositForBurn' : 'placeOrder'

            const functionParams = await getSwapFunctionParameters(
                exchangeType,
                destinationAddress,
                sellData,
                buyData,
                quote as IQuoteData,
                false,
                availableNetworks
            )

            const hash = await writeContractAsync({
                abi: swapContract.abi,
                address: swapContract.address,
                functionName,
                args: functionParams || [],
            })

            if (!hash) {
                throw new Error('Could not send transaction.')
            }

            updateHistory(null, {
                exchangeId,
                currentStep: StepNames.CONFIRM_SWAP_PENDING,
                result: {
                    status: ResultCode.pending,
                    message: 'Waiting for confirmation.',
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            const receipt: any = await waitForTransactionReceipt(hash)

            updateHistory(null, {
                exchangeId,
                currentStep: StepNames.MATCHING,
                result: {
                    status: ResultCode.pending,
                    message: 'Order matching in progress.',
                    transactionETA: 60,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            let result

            if (exchangeType === ExchangeType.cctp) {
                result = await handleCctpExchange(hash, exchangeId, sellData, buyData)
            } else {
                const encodedTopics = encodeEventTopics({
                    abi: swapContract.abi,
                    eventName: 'OrderPlaced',
                })[0]

                const decodedData = processReceipt(
                    receipt,
                    encodedTopics,
                    availableNetworks[sellData.chainId].name,
                    swapContract.abi
                )

                try {
                    result = await apiSendOrderData(decodedData[0], referralCode.code, referrer)
                } catch (error) {
                    // First check if we're on testnet
                    if (isFeatureEnabled('USE_TESTNETS_ONLY')) {
                        // Only check for rate limiting on testnet
                        console.log('Checking rate limit on error:', error)
                        const isRateLimited =
                            (error as any)?.response?.status === 429 ||
                            (error as any)?.status === 429 ||
                            (error as Error)?.message?.includes('status code 429')

                        if (isRateLimited) {
                            console.log('Rate limited on testnet')
                            // Extract wait time if available, otherwise use dynamic config
                            let waitTime = rateLimitConfig.windowSeconds
                            const detail = (error as any)?.response?.data?.detail || (error as any)?.data?.detail

                            if (typeof detail === 'string') {
                                const match = detail.match(/Too many requests\. Please wait (\d+\.?\d*) seconds\./)
                                if (match && match[1]) {
                                    waitTime = Math.ceil(parseFloat(match[1]))
                                }
                            }

                            // Throw a specific rate limit error with wait time
                            throw new Error(`Rate limited. Please try again in ${waitTime} seconds.`)
                        }
                    }

                    // Default case for production or non-rate-limit errors
                    throw new Error('Cannot send order data')
                }
            }

            if (result?.status === ResultCode.failure) {
                // This error happens when order is placed on chain,
                // but backend cannot process it
                throw new Error('Cannot send order data')
            }

            updateHistory(exchangeId, {
                currentStep: StepNames.MATCH_CREATED,
                result: {
                    status: ResultCode.pending,
                    message: 'Order accepted. Order matching in-progress.',
                    transactionETA: 60,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            // Update Redux dispatch calls to Zustand
            setOrderId(result?.id?.toString() || '')

            // small delay to start timer, without instant Match Executed
            await new Promise(r => setTimeout(r, 250))

            updateHistory(exchangeId, {
                currentStep: StepNames.MATCH_EXECUTED,
                orderId: typeof result?.id === 'string' ? result.id : '',
                result: {
                    status: ResultCode.pending,
                    message: 'Order successfully sent.',
                    transactionETA: result?.eta || 10,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            onSuccess()
        } catch (error) {
            logger.error(`EVM swap failed (chainId: ${sellData.chainId}):`, error)
            throw error
        } finally {
            setWaitingForConnectedWalletInteraction(false)
        }
    }

    return {
        executeSwap,
        handleApproval,
        handleWrap,
        handleUnwrap,
    }
}
